import React from 'react';
import {
  Box,
  Typography,
  Tooltip
} from '@material-ui/core';
import { useTimelineViewStyles } from '../styles/timeline-view.styles';
import { TimeMarker, ProcessedMilestone, Pipeline, Step } from '../types';
import { 
  createPipelineLayoutConfig, 
  generatePipelineTimeMarkers, 
  calculateTimelineMinWidth,
  calculateMilestoneLevels,
  calculateMilestoneContainerHeight
} from '../utils/positioning';
import { 
  PipelineHeadersRow, 
  PipelineStepsContainer 
} from './PipelineComponents';
import { 
  PipelineTimeMarkers, 
  RegularTimeMarkers 
} from './TimeMarkerComponents';
import { 
  MilestoneContainer 
} from './MilestoneComponents';
/**
 * Props interface for the TimeMarkersRow component
 */
interface TimeMarkersRowProps {
  /** Array of time markers for fallback display */
  timeMarkers: TimeMarker[];
  /** Optional array of pipelines for primary time marker generation */
  pipelines?: any[];
}
/**
 * TimeMarkersRow Component
 * 
 * Renders the time markers row with intelligent selection between pipeline-based
 * and regular time markers. Prioritizes pipeline-based markers when available,
 * falling back to regular time markers for consistent timeline visualization.
 * 
 * @component
 * @param props - The component props
 * @param props.timeMarkers - Time markers for fallback display
 * @param props.pipelines - Optional pipelines for primary marker generation
 * 
 * @returns Appropriate time markers based on available data
 */
export const TimeMarkersRow = React.memo(({ timeMarkers, pipelines }: TimeMarkersRowProps) => {
  // Use pipeline-based markers if pipelines are available
  if (pipelines && pipelines.length > 0) {
    const pipelineMarkers = React.useMemo(() => generatePipelineTimeMarkers(pipelines), [pipelines]);
    const totalMinWidth = React.useMemo(() => calculateTimelineMinWidth(pipelines.length), [pipelines.length]);
    
    return (
      <PipelineTimeMarkers 
        pipelineMarkers={pipelineMarkers} 
        totalMinWidth={totalMinWidth} 
      />
    );
  }
  
  // Fallback to regular time markers
  return <RegularTimeMarkers timeMarkers={timeMarkers} />;
});

TimeMarkersRow.displayName = 'TimeMarkersRow';

/**
 * Props interface for the MilestonesRow component
 */
interface MilestonesRowProps {
  /** Array of processed milestones to display */
  milestones: ProcessedMilestone[];
  /** Array of time markers for alignment reference */
  timeMarkers: TimeMarker[];
  /** Optional array of pipelines for layout calculations */
  pipelines?: any[];
  /** Optional callback for milestone click events */
  onMilestoneClick?: (milestone: ProcessedMilestone) => void;
  /** Optional array of currently selected milestones */
  selectedMilestones?: ProcessedMilestone[];
}
/**
 * MilestonesRow Component
 * 
 * Renders the milestones row with intelligent level calculation and container
 * sizing. Automatically calculates milestone positioning levels to prevent
 * overlaps and determines appropriate container height based on milestone
 * distribution and pipeline layout.
 * 
 * @component
 * @param props - The component props
 * @param props.milestones - Processed milestones to display
 * @param props.timeMarkers - Time markers for alignment reference
 * @param props.pipelines - Optional pipelines for layout calculations
 * 
 * @returns A milestone container with properly positioned and leveled milestones
 */
export const MilestonesRow = React.memo(({ 
  milestones, 
  timeMarkers,
  pipelines,
  onMilestoneClick,
  selectedMilestones
}: MilestonesRowProps) => {
  // Calculate levels for overlapping milestones using utility function
  const milestonesWithLevels = React.useMemo(() => calculateMilestoneLevels(milestones), [milestones]);
  
  // Calculate container height based on milestone levels
  const containerHeight = React.useMemo(() => calculateMilestoneContainerHeight(milestonesWithLevels), [milestonesWithLevels]);
  
  // Calculate minimum width based on pipelines
  const minWidth = React.useMemo(() => {
    return pipelines && pipelines.length > 0 
      ? `${calculateTimelineMinWidth(pipelines.length)}px` 
      : 'fit-content';
  }, [pipelines]);
  
  return (
    <MilestoneContainer
      milestonesWithLevels={milestonesWithLevels}
      timeMarkers={timeMarkers}
      pipelines={pipelines}
      containerHeight={containerHeight}
      minWidth={minWidth}
      onMilestoneClick={onMilestoneClick}
      selectedMilestones={selectedMilestones}
    />
  );
});

MilestonesRow.displayName = 'MilestonesRow';

/**
 * Props interface for the StepTooltip component
 */
interface StepTooltipProps {
  /** Step object containing details for tooltip display */
  step: Step;
  /** Child element that triggers the tooltip */
  children: React.ReactElement;
}
/**
 * StepTooltip Component
 * 
 * Provides an interactive tooltip for pipeline steps displaying detailed
 * information including step name, metrics, and status. Features structured
 * layout with proper typography hierarchy and consistent styling.
 * 
 * @component
 * @param props - The component props
 * @param props.step - Step object with name, metrics, and status
 * @param props.children - Element that triggers the tooltip on hover
 * 
 * @returns A tooltip wrapper with detailed step information
 */
export const StepTooltip = React.memo(({ step, children }: StepTooltipProps) => {
  const classes = useTimelineViewStyles();
  
  return (
    <Tooltip
      title={
        <Box className={classes.tooltipContent}>
          <Typography variant="subtitle2" className={classes.tooltipTitle}>
            {step.name}
          </Typography>
          {Object.entries(step.metrics).map(([key, value]) => (
            <Box key={key} className={classes.tooltipMetric}>
              <Typography variant="caption" className={classes.tooltipMetricKey}>
                {key}:
              </Typography>
              <Typography variant="caption" className={classes.tooltipMetricValue}>
                {value}
              </Typography>
            </Box>
          ))}
          <Typography variant="caption" className={classes.tooltipStatus}>
            Status: {step.status}
          </Typography>
        </Box>
      }
      arrow
      placement="top"
    >
      {children}
    </Tooltip>
  );
});

StepTooltip.displayName = 'StepTooltip';

/**
 * Props interface for the HorizontalPipelineLayout component
 */
interface HorizontalPipelineLayoutProps {
  /** Array of pipeline objects to display */
  pipelines: Pipeline[];
  /** Array of time markers for reference */
  timeMarkers: TimeMarker[];
}
/**
 * HorizontalPipelineLayout Component
 * 
 * Renders the complete horizontal pipeline layout with headers and step
 * containers. Manages layout configuration and coordinates the display
 * of pipeline headers and their corresponding step containers in a
 * horizontally organized timeline view.
 * 
 * @component
 * @param props - The component props
 * @param props.pipelines - Pipeline objects to display
 * @param props.timeMarkers - Time markers for reference
 * 
 * @returns A complete horizontal pipeline layout with headers and steps
 */
export const HorizontalPipelineLayout = React.memo(({ 
  pipelines, 
  timeMarkers 
}: HorizontalPipelineLayoutProps) => {
  const classes = useTimelineViewStyles();
  const [highlightedTraceId, setHighlightedTraceId] = React.useState<string | null>(null);
  
  // Create layout configuration
  const layoutConfig = React.useMemo(() => createPipelineLayoutConfig(pipelines.length), [pipelines.length]);
  
  return (
    <Box className={classes.horizontalPipelineContainer}>
      <PipelineHeadersRow 
        pipelines={pipelines} 
        layoutConfig={layoutConfig} 
      />
      <PipelineStepsContainer 
        pipelines={pipelines} 
        layoutConfig={layoutConfig}
        highlightedTraceId={highlightedTraceId}
        onStepHover={setHighlightedTraceId}
      />
    </Box>
  );
});

HorizontalPipelineLayout.displayName = 'HorizontalPipelineLayout';
